import { useState, useCallback, useMemo } from 'react';
import releaseNotes from '../release-notes.json';
import { SEEN_FEATURES_KEY, APP_VERSION } from '../release-notes-constants';
import type { ReleaseNotesData } from '../release-notes-types';

interface UseFeatureBadgeReturn {
  isNew: boolean;
  markSeen: () => void;
}

/** Build version-scoped localStorage key so badges reset on new versions */
function getSeenFeaturesKey(): string {
  return APP_VERSION ? `${SEEN_FEATURES_KEY}:${APP_VERSION}` : SEEN_FEATURES_KEY;
}

function readSeenFeatures(): string[] {
  try {
    const raw = localStorage.getItem(getSeenFeaturesKey());
    if (!raw) return [];
    return JSON.parse(raw) as string[];
  } catch {
    return [];
  }
}

function writeSeenFeatures(features: string[]): void {
  try {
    localStorage.setItem(getSeenFeaturesKey(), JSON.stringify(features));
  } catch {
    // quota exceeded or private mode — ignore
  }
}

export function useFeatureBadge(featureId: string): UseFeatureBadgeReturn {
  const allBadges = useMemo(() => {
    const data = releaseNotes as ReleaseNotesData;
    const set = new Set<string>();
    for (const version of data) {
      for (const badge of version.badges) {
        set.add(badge);
      }
    }
    return set;
  }, []);

  const [seenFeatures, setSeenFeatures] = useState<string[]>(readSeenFeatures);

  const isNew = allBadges.has(featureId) && !seenFeatures.includes(featureId);

  const markSeen = useCallback(() => {
    setSeenFeatures((prev) => {
      if (prev.includes(featureId)) return prev;
      const next = [...prev, featureId];
      writeSeenFeatures(next);
      return next;
    });
  }, [featureId]);

  return { isNew, markSeen };
}
